"use client";

import * as React from "react";
import { usePathname } from "@/lib/router/navigation";

/**
 * A client-side route change replaces the page without a document load, so a
 * screen reader announces NOTHING and keyboard focus stays wherever it was —
 * usually on a sidebar link, several regions away from the new content.
 *
 * On each pathname change this moves focus to the main region and announces the
 * new page's heading in a live region. Both are needed: focus alone is silent
 * in some readers, and an announcement alone leaves the keyboard user stranded
 * (NFR-32, WCAG 2.1 §2.4.3).
 *
 * Skips the very first render — the initial page load is announced by the
 * browser itself, and stealing focus there would be its own bug.
 */
export function useRouteAnnouncement(mainId = "main-content"): string {
  const pathname = usePathname();
  const [announcement, setAnnouncement] = React.useState("");
  const firstRender = React.useRef(true);

  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const main = document.getElementById(mainId);
    if (main) main.focus({ preventScroll: true });

    // Read after paint, so the new page's heading exists to be read.
    const frame = window.requestAnimationFrame(() => {
      const heading = document.querySelector("main h1")?.textContent?.trim();
      setAnnouncement(heading ? `${heading} — page loaded` : "Page loaded");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pathname, mainId]);

  return announcement;
}
